<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.customer', 2) },
      { label: 'Customer Contact Details', to: '/reports/customers/contact-details' }
    ]"
  >
    <template #right>
      <div class="flex items-stretch lg:items-center gap-2 flex-col lg:flex-row flex-wrap w-full lg:w-auto">
        <DealershipSelect
          v-model="dealershipSelected"
          mode="single"
          :chips="false"
          placeholder="Select a Dealership"
          @change="onDealershipChange"
        />

        <Button
          class="shrink-0"
          label="Update"
          :is-loading="customerDataApiRequest.isLoading.value"
          :disabled="!hasDealershipSelected"
          @click="onDealershipChange"
        />
      </div>
    </template>
  </PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Customer Contact Details"
      :value="customerData"
      removable-sort
      sort-field="id"
      :sort-order="-1"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['id', 'customerName']"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-report-customer-contact-details"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="customerDataApiRequest.isLoading.value"
    >
      <template #empty>
        <Empty
          :title="hasDealershipSelected ? 'No Customers Found' : 'Select a dealership'"
          :icon="hasDealershipSelected ? 'address-card' : 'building'"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV()"
            />
          </span>
        </div>
      </template>

      <!-- <Column
        field="id"
        header="ID"
        :sortable="true"
      /> -->

      <Column
        field="customerName"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          <router-link
            :to="'/agreement/' + getAgreementId(data)"
            class="text-primary link whitespace-nowrap"
          >
            {{ data.customerName || data.id }}
          </router-link>
        </template>
      </Column>

      <Column
        field="mobile"
        header="Mobile"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <ContactMethodChip :value="data.mobile" />
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="contactMethodOptions"
            :chips="false"
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-40"
          />
        </template>
      </Column>

      <Column
        field="telephone"
        header="Telephone"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <ContactMethodChip :value="data.telephone" />
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="contactMethodOptions"
            :chips="false"
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-40"
          />
        </template>
      </Column>

      <Column
        field="email"
        header="Email"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <ContactMethodChip :value="data.email" />
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="contactMethodOptions"
            :chips="false"
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-40"
          />
        </template>
      </Column>

      <Column
        field="address"
        header="Address"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <ContactMethodChip :value="data.address" />
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="contactMethodOptions"
            :chips="false"
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-40"
          />
        </template>
      </Column>

      <Column
        field="contactDetailsStatus"
        header="Contact Details"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <Chip
            :severity="data.contactDetailsCount === 0 ? 'danger' : 'success'"
            size="sm"
          >
            {{ data.contactDetailsStatus }}
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="contactDetailsStatusOptions"
            :chips="false"
            mode="multiple"
            value-key="value"
            label-key="label"
            placeholder="All"
            class="min-w-40"
          />
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup lang="ts">
// components
import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import Select from '@/components/forms/Select.vue'
import DealershipSelect from '@/components/forms/DealershipSelect.vue'
import Empty from '@/components/empty/Empty.vue'

// composables
import useApiRequest from '@/composables/useApiRequest'

// stores
import { useAuthStore } from '@/stores/AuthStore'

import { alertToast } from '@/utilities/notification'

// vue
import { computed, defineComponent, h, onMounted, ref } from 'vue'

// primevue
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'

type ContactMethodValue = 0 | 1
type ContactDetailsStatusValue = 'No Details' | '1 Detail' | '2 Details' | '3 Details' | '4 Details'

interface AllowedDealership {
  id: number
  name: string
  manufacturers: number[]
}

interface ContactValue {
  id: number | null
  type: string | null
  value?: string
}

interface ContactAddressValue {
  id: number | null
  type: string | null
  formatted: string | null
}

interface CustomerContactDetailsApiItem {
  id: number
  agreementId?: number
  customerName: string | null
  totals: {
    phone: number
    email: number
    address: number
  }
  values: {
    phone: ContactValue[]
    email: ContactValue[]
    address: ContactAddressValue[]
  }
}

interface CustomerContactDetailsRow {
  id: number
  agreementId?: number
  customerName: string | null
  mobile: ContactMethodValue
  telephone: ContactMethodValue
  email: ContactMethodValue
  address: ContactMethodValue
  contactDetailsCount: number
  contactDetailsStatus: ContactDetailsStatusValue
}

interface ContactMethodFilter {
  value: ContactMethodValue[] | undefined
  matchMode: string
}

interface ContactDetailsStatusFilter {
  value: ContactDetailsStatusValue[] | undefined
  matchMode: string
}

interface DataTableFilters {
  global: {
    value: string | undefined
    matchMode: string
  }
  mobile: ContactMethodFilter
  telephone: ContactMethodFilter
  email: ContactMethodFilter
  address: ContactMethodFilter
  contactDetailsStatus: ContactDetailsStatusFilter
}

/** The local storage key for retaining the selected dealership between visits. */
const dealershipStorageKey = 'report-customer-contact-details-dealership-selected'

/** The session storage key used by PrimeVue to persist table state. */
const tableStateKey = 'dt-report-customer-contact-details'

/** Filter options for contact method existence columns. */
const contactMethodOptions: Array<{ value: ContactMethodValue; label: string }> = [
  { value: 1, label: 'Exists' },
  { value: 0, label: 'Missing' }
]

/** Filter options for contact detail completeness. */
const contactDetailsStatusOptions: Array<{ value: ContactDetailsStatusValue; label: string }> = [
  { value: 'No Details', label: 'No Details' },
  { value: '1 Detail', label: '1 Detail' },
  { value: '2 Details', label: '2 Details' },
  { value: '3 Details', label: '3 Details' },
  { value: '4 Details', label: '4 Details' }
]

/** Component used by each contact method column to render the status chip consistently. */
const ContactMethodChip = defineComponent({
  name: 'ContactMethodChip',
  props: {
    value: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    return () =>
      h(
        Chip,
        {
          severity: props.value === 1 ? 'success' : 'danger',
          size: 'sm'
        },
        () => String(props.value)
      )
  }
})

const authStore = useAuthStore()

/** The currently selected dealership ID for the report query. */
const dealershipSelected = ref<number | undefined>(getStoredDealershipId())

/** The user's allowed dealerships, used for validation and auto-selection. */
const dealershipsAllowed = ref<AllowedDealership[]>(authStore.accessAllowed.dealerships as AllowedDealership[])

/** PrimeVue datatable component reference used for CSV export. */
const dt = ref<{ exportCSV: () => void } | null>(null)

/** Normalized report rows displayed in the datatable. */
const customerData = ref<CustomerContactDetailsRow[]>([])

/** API request state for fetching customer contact detail report data. */
const customerDataApiRequest = useApiRequest()

/** True when a valid dealership has been selected. */
const hasDealershipSelected = computed((): boolean => typeof dealershipSelected.value === 'number')

/** Table filters for global search and contact method existence columns. */
const filters = ref<DataTableFilters>(createInitialFilters())

onMounted(async (): Promise<void> => {
  ensureValidDealershipSelection()

  // Fetch immediately when a persisted or auto-selected dealership is available.
  if (hasDealershipSelected.value) {
    await fetchCustomerData()
  }
})

/**
 * Reads the selected dealership ID from local storage.
 *
 * @returns The stored dealership ID when valid, otherwise null.
 */
function getStoredDealershipId(): number | undefined {
  const storedValue = localStorage.getItem(dealershipStorageKey)

  // No previous selection has been saved.
  if (storedValue === null) {
    return undefined
  }

  try {
    const parsedDealershipId = JSON.parse(storedValue) as unknown

    // Only numeric dealership IDs can be used for the report endpoint.
    if (typeof parsedDealershipId !== 'number') {
      return undefined
    }

    return parsedDealershipId
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Invalid stored dealership'
    alertToast('Failed to load saved dealership', errorMessage, 'error')
    return undefined
  }
}

/**
 * Creates the default datatable filters.
 *
 * @returns The initial PrimeVue filter configuration.
 */
function createInitialFilters(): DataTableFilters {
  return {
    global: { value: undefined, matchMode: FilterMatchMode.CONTAINS },
    mobile: { value: undefined, matchMode: FilterMatchMode.IN },
    telephone: { value: undefined, matchMode: FilterMatchMode.IN },
    email: { value: undefined, matchMode: FilterMatchMode.IN },
    address: { value: undefined, matchMode: FilterMatchMode.IN },
    contactDetailsStatus: { value: undefined, matchMode: FilterMatchMode.IN }
  }
}

/**
 * Clears all table filters and removes the saved datatable state.
 */
function clearFilters(): void {
  sessionStorage.removeItem(tableStateKey)
  filters.value = createInitialFilters()
}

/**
 * Stores the selected dealership and refreshes the report.
 */
async function onDealershipChange(): Promise<void> {
  persistDealershipSelection()
  await fetchCustomerData()
}

/**
 * Ensures the selected dealership is still allowed for the current user.
 */
function ensureValidDealershipSelection(): void {
  const allowedDealershipIds = dealershipsAllowed.value.map(dealership => dealership.id)

  // Clear persisted selections that are no longer available to this user.
  if (dealershipSelected.value !== undefined && !allowedDealershipIds.includes(dealershipSelected.value)) {
    dealershipSelected.value = undefined
  }

  // Auto-select the only available dealership when the user has exactly one.
  if (dealershipsAllowed.value.length === 1 && dealershipSelected.value === undefined) {
    dealershipSelected.value = dealershipsAllowed.value[0].id
  }

  persistDealershipSelection()
}

/**
 * Persists the selected dealership ID for the next report visit.
 */
function persistDealershipSelection(): void {
  // Remove the saved value when there is no dealership selected.
  if (dealershipSelected.value === undefined) {
    localStorage.removeItem(dealershipStorageKey)
    return
  }

  localStorage.setItem(dealershipStorageKey, JSON.stringify(dealershipSelected.value))
}

/**
 * Fetches and normalizes customer contact detail report rows for the selected dealership.
 */
async function fetchCustomerData(): Promise<void> {
  // The endpoint requires a selected dealership ID.
  if (!hasDealershipSelected.value || dealershipSelected.value === undefined) {
    customerData.value = []
    return
  }

  try {
    const response = await customerDataApiRequest.send<CustomerContactDetailsApiItem[]>({
      endpoint: '/v1/reports/customers/contact-details',
      params: {
        dealershipId: dealershipSelected.value,
        includeDetails: true
      }
    })

    // Only replace table data when the API reports a successful response.
    if (response.success && Array.isArray(response.data)) {
      customerData.value = response.data.map(createCustomerContactDetailsRow).filter(hasMissingContactDetails)
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    alertToast('Failed to fetch customer contact details', errorMessage, 'error')
  }
}

/**
 * Converts one API record into fields that can be displayed, filtered, and exported.
 *
 * @param item The raw API report item.
 * @returns A normalized customer contact details table row.
 */
function createCustomerContactDetailsRow(item: CustomerContactDetailsApiItem): CustomerContactDetailsRow {
  const row = {
    id: item.id,
    agreementId: item.agreementId,
    customerName: item.customerName,
    mobile: hasPhoneType(item.values.phone, 'mobile'),
    telephone: hasPhoneType(item.values.phone, 'landline'),
    email: getContactMethodValue(item.totals.email),
    address: getContactMethodValue(item.totals.address),
    contactDetailsCount: 0,
    contactDetailsStatus: 'No Details' as ContactDetailsStatusValue
  }
  const contactDetailsCount = getContactDetailsCount(row)

  return {
    ...row,
    contactDetailsCount,
    contactDetailsStatus: getContactDetailsStatus(contactDetailsCount)
  }
}

/**
 * Checks whether a row has at least one missing contact detail.
 *
 * @param row The normalized report row.
 * @returns True when any contact method is missing.
 */
function hasMissingContactDetails(row: CustomerContactDetailsRow): boolean {
  return row.mobile === 0 || row.telephone === 0 || row.email === 0 || row.address === 0
}

/**
 * Checks whether a row has any usable contact details.
 *
 * @param row The normalized report row.
 * @returns True when at least one contact method exists.
 */
function getContactDetailsCount(row: Pick<CustomerContactDetailsRow, 'mobile' | 'telephone' | 'email' | 'address'>): number {
  return row.mobile + row.telephone + row.email + row.address
}

/**
 * Gets the human-readable contact details completeness status.
 *
 * @param count The number of available contact detail categories.
 * @returns A status label for the contact detail count.
 */
function getContactDetailsStatus(count: number): ContactDetailsStatusValue {
  if (count === 0) {
    return 'No Details'
  }

  if (count === 1) {
    return '1 Detail'
  }

  return `${count} Details` as ContactDetailsStatusValue
}

/**
 * Checks whether a contact method value exists for a specific phone type.
 *
 * @param phones Phone values returned by the API.
 * @param phoneType The phone type to check.
 * @returns 1 when a matching value exists, otherwise 0.
 */
function hasPhoneType(phones: ContactValue[], phoneType: 'mobile' | 'landline'): ContactMethodValue {
  return phones.some(phone => phone.type === phoneType && Boolean(phone.value)) ? 1 : 0
}

/**
 * Converts a contact detail count into a filterable contact method flag.
 *
 * @param count The number of available contact details.
 * @returns 1 when at least one contact detail exists, otherwise 0.
 */
function getContactMethodValue(count: number): ContactMethodValue {
  return count > 0 ? 1 : 0
}

/**
 * Gets the agreement route ID for a row.
 *
 * @param row The normalized report row.
 * @returns The agreement ID when present, otherwise the row ID.
 */
function getAgreementId(row: CustomerContactDetailsRow): number {
  return row.agreementId ?? row.id
}

/**
 * Exports the current datatable data to CSV.
 */
function exportCSV(): void {
  dt.value?.exportCSV()
}
</script>
